import ConfiguracaoService from "@/services/ConfiguracaoService";
import { useEffect, useRef, useState } from "react";
import InserirCreditosModal from "./modal";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FormasPagamento } from "@/dtos/FormasPagamento";
import CompraService from "@/services/CompraService";
import { QrCodeGeradoDto } from "@/dtos/QrCodeGeradoDto";
import { useToast } from "@/components/ui/use-toast";
import { QrCode } from "@/components/QrCode";
import { DialogNavegarPosPagamentoInserirCreditos } from "./DialogNavegarPosPagamentoInserirCreditos";
import { labelSingularPluralParenteses } from "@/utils/copyTituloBilhete";

export function noop() { }

export function InserirCreditosPage() {
  const textos = {
    titulo: 'Inserir Créditos via PIX',
    tituloCreditos: 'Selecione o valor de crédito desejado',
    tituloInformacoesImportantes: 'Informações importantes',
    informacoesImportantes: [
      'Após o pagamento da Fatura a liberação do Crédito pode levar alguns minutos;'
    ],
    comoFunciona: [
      "Escolha o valor de crédito que você deseja adquirir.",
      "Para realizar a aquisição, clique em 'Comprar'.",
      "Uma fatura com o valor escolhido é exibido em tela e também enviado para o seu e-mail cadastrado. Copie o código 'Pix Copia e Cola' e utilize a opção 'Pix > Pix Copia e Cola' no aplicativo do seu banco.",
      "Ao identificarmos o pagamento da sua fatura, você receberá um e-mail de confirmação. A partir daí, ao entrar no nosso site ou aplicativo, o valor dos seus créditos aparecerá ao lado do seu nome.",
      `Você já pode usar os créditos para adquirir seu(s) ${labelSingularPluralParenteses()}, contribuindo com a nossa associação filantrópica e concorrendo a valiosos prêmios toda semana!`
    ],
    informacoesImportantesCompra: [
      'O prazo de aprovação pode levar alguns minutos;',
      'Caso o vencimento seja domingo o feriado, o pagamento pode ser realizado no próximo dia útil;',
      'Após o vencimento do prazo e a não confirmação de pagamento, um novo código deve ser gerado.'
    ],
  };

  const formatarValor = (valor: number) => valor.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });

  const [valorSelecionado, setValorSelecionado] = useState(0);
  const [valores, setValores] = useState<number[]>([]);
  const [botaoComprarHabilitado, setBotaoComprarHabilitado] = useState(false);
  const [exibirModal, setExibirModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [textosModal, setTextosModal] = useState<string[]>([]);
  const [tituloModal, setTituloModal] = useState<string>('');
  const [dadosQrCode, setDadosQrCode] = useState<QrCodeGeradoDto>();
  const [confirmacaoModal, setConfirmacaoModal] = useState(() => () => { });
  const [realizandoCompra, setRealizandoCompra] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast()
  const [exibirDialog, setExibirDialog] = useState(false);


  useEffect(() => {
    async function loadingInicial() {
      try {
        setLoading(true)
        const [{ Data: result1 }, { Data: result2 }] = await Promise.all([ConfiguracaoService.buscarValoresCredito(), CompraService.BuscarUltimoBoletoEmAberto()])

        setValores(result1);

        if (result2 != null && !result2.Compra) {
          timerRef.current = setTimeout(() => {
            setExibirDialog(true);
          }, 1000 * 30)

          toast({
            variant: 'destructive',
            description: 'Você possui fatura aguardando pagamento!',
            duration: 3000
          })
          setDadosQrCode(result2)
        }
      } catch (error) {

      } finally {
        setLoading(false)
      }
    };

    loadingInicial();

    return () => {
      if (timerRef.current)
        clearTimeout(timerRef.current)
    }
  }, []);

  const toggleModal = () => {
    setExibirModal(!exibirModal);
  }

  const escolherValor = (valor: number) => {
    if (!valor || !valores.find((val) => val === valor)) return;

    setValorSelecionado(valor);

    const divEscolhida = document.getElementById(valor.toString());

    if (!divEscolhida) return;

    divEscolhida.classList.add('efeito-inserir-creditos-selecionado');
    divEscolhida.classList.remove('efeito-inserir-creditos');

    setBotaoComprarHabilitado(true);

    deselecionarDivs(divEscolhida.id);
  }

  const deselecionarDivs = (exceptId: string) => {
    const divAbertas = document.getElementsByClassName('efeito-inserir-creditos-selecionado');

    if (!divAbertas) return;

    Array.from(divAbertas).filter((div) => div.id !== exceptId).map((div) => {
      div.classList.remove('efeito-inserir-creditos-selecionado');
      div.classList.add('efeito-inserir-creditos');
    });
  }

  const abrirModalComoFunciona = () => {
    setTituloModal(tituloComoFunciona);
    setTextosModal(textos['comoFunciona']);
    setConfirmacaoModal(noop);

    toggleModal();
  }

  const abrirModalCancelarFatura = () => {
    setTituloModal('Cancelar Fatura');
    setTextosModal(['Você tem certeza disso?']);
    setConfirmacaoModal(() => handleCancelarFatura);

    toggleModal();
  }

  async function handleComprar() {
    try {
      setRealizandoCompra(true);
      const { Data: result } = await CompraService.InserirCreditos({ ValorCredito: valorSelecionado, TipoFatura: FormasPagamento.PixInserirCredito })
      setDadosQrCode(result);

      timerRef.current = setTimeout(() => {
        setExibirDialog(true);
      }, 5000)

      toast({
        variant: 'success',
        description: 'QRCode gerado com sucesso!',
        duration: 3000
      })
    } catch (error: any) {
      toast({
        variant: 'destructive',
        description: error,
        duration: 4000
      });
    } finally {
      setRealizandoCompra(false);
    }
  }

  async function handleCancelarFatura() {
    try {
      await CompraService.CancelarFatura(dadosQrCode?.IdFatura ?? '');
      setDadosQrCode(undefined);
      setValorSelecionado(0);

      toast({
        variant: 'success',
        description: 'Fatura cancelada com sucesso!',
        duration: 3000
      })
    } catch (error: any) {
      toast({
        variant: 'destructive',
        description: error,
        duration: 4000
      });
    }
  }

  const tituloComoFunciona = 'Como funciona?';

  return (
    <div className="p-3">
      {exibirModal &&
        <InserirCreditosModal
          titulo={tituloModal}
          conteudo={textosModal}
          exibir={exibirModal}
          toggle={toggleModal}
          acaoConfirmacao={confirmacaoModal}
        />
      }

      {
        dadosQrCode && (
          <QrCode
            DataVencimento={dadosQrCode.DataVencimento}
            Valor={dadosQrCode.Valor}
            QrCode={dadosQrCode.QrCode}
            QrCodeText={dadosQrCode.QrCodeText}
            abrirModalCancelarFatura={abrirModalCancelarFatura}
            IdFatura={dadosQrCode.IdFatura}
          />
        )
      }

      {
        exibirDialog && (
          <DialogNavegarPosPagamentoInserirCreditos toggleDialog={() => { setExibirDialog(!exibirDialog) }} exibir={exibirDialog} />
        )
      }

      {
        !dadosQrCode && (
          <>
            <h1 className="text-primary-foreground text-2xl uppercase font-black" title={textos['titulo']}>{textos['titulo']}</h1>
            <h2 title={textos['tituloCreditos']} className='font-normal text-lg'>{textos['tituloCreditos']}:</h2>

            <div className="flex flex-wrap gap-4 my-4 mx-auto md:w-8/12 justify-center">
              {
                valores && valores.map((val, i) => {
                  const valorFormatado = formatarValor(val);

                  return (
                    <div
                      key={`valor-${i + 1}`}
                      id={`${val}`}
                      title={valorFormatado}
                      className="efeito-inserir-creditos w-36 text-center text-3xl font-normal cursor-pointer shadow-lg transition-shadow p-2 relative rounded-md hover:shadow-xl"
                      onClick={() => escolherValor(val)}>
                      <span>{valorFormatado}</span>
                    </div>
                  )
                })
              }

              {
                loading && <>
                  <div className="flex w-full justify-center">
                    Carregando informações
                  </div>
                  <div className="flex w-full justify-center mt-5 mb-10 gap-4">
                    <Skeleton className="h-16 w-32" />
                    <Skeleton className="h-16 w-32" />
                    <Skeleton className="h-16 w-32" />
                    <Skeleton className="h-16 w-32" />
                    <Skeleton className="h-16 w-32" />
                    <Skeleton className="h-16 w-32" />
                  </div>
                </>
              }
            </div>

            <div className="flex justify-center">
              <Button
                id="comprar"
                size="xl"
                variant="foreground"
                className="sm:ml-2"
                title='Comprar'
                onClick={handleComprar}
                disabled={!botaoComprarHabilitado || realizandoCompra}>
                {
                  realizandoCompra ? 'Aguarde...' : 'Comprar'
                }
              </Button>
            </div>

            <div id='informacoes-importantes' className="mt-8">
              <h3 className='text-xl font-semibold uppercase'>{textos['tituloInformacoesImportantes']}:</h3>
              <ul className="list-square ml-5">
                {
                  textos['informacoesImportantes'].map((info, i) =>
                    <li key={`informacao-${i + 1}`} title={info} className='font-normal'>{info}</li>
                  )
                }
              </ul>
            </div>
            <div id="footer" className="sm:p-2 sm:text-right text-center">
              <Button variant="outline" className="sm:m-3 m-2" onClick={abrirModalComoFunciona}>{tituloComoFunciona}</Button>
            </div>
          </>
        )
      }
    </div>
  );
}